/**
 * Contact Details Service for Outlook Add-in
 * Handles contact details (email, phone) CRUD operations
 */

import apiClient from '../utils/apiClient';

class ContactDetailsService {
  /**
   * Create a new contact detail
   * @param {Object} detailData - Contact detail data
   * @returns {Promise<Object>} Created contact detail
   */
  async createContactDetail(detailData) {
    try {
      const response = await apiClient.post('/contact-details', detailData);
      
      if (response.success) {
        return {
          success: true,
          data: response.data,
          message: response.message
        };
      } else {
        throw new Error(response.message || 'Failed to create contact detail');
      }
    } catch (error) {
      console.error('Error creating contact detail:', error);
      throw error;
    }
  }

  /**
   * Update an existing contact detail
   * @param {string} detailId - Contact detail ID
   * @param {Object} detailData - Contact detail data to update
   * @returns {Promise<Object>} Updated contact detail
   */
  async updateContactDetail(detailId, detailData) {
    try {
      const response = await apiClient.put(`/contact-details/${detailId}`, detailData);
      
      if (response.success) {
        return {
          success: true,
          data: response.data,
          message: response.message
        };
      } else {
        throw new Error(response.message || 'Failed to update contact detail');
      }
    } catch (error) {
      console.error('Error updating contact detail:', error);
      throw error;
    }
  }

  /**
   * Delete a contact detail
   * @param {string} detailId - Contact detail ID
   * @returns {Promise<Object>} Delete result
   */
  async deleteContactDetail(detailId) {
    try {
      const response = await apiClient.delete(`/contact-details/${detailId}`);
      
      if (response.success) {
        return {
          success: true,
          message: response.message
        };
      } else {
        throw new Error(response.message || 'Failed to delete contact detail');
      }
    } catch (error) {
      console.error('Error deleting contact detail:', error);
      throw error;
    }
  }

  /**
   * Get contact details by contact ID
   * @param {string} contactId - Contact ID
   * @returns {Promise<Object>} Contact details
   */
  async getContactDetailsByContactId(contactId) {
    try {
      // Use getAllContactDetails with contact_id filter
      const response = await apiClient.get(`/contact-details?contact_id=${contactId}`);
      
      if (response.success) {
        return {
          success: true,
          data: response.data,
          message: response.message
        };
      } else {
        throw new Error(response.message || 'Failed to fetch contact details');
      }
    } catch (error) {
      console.error('Error fetching contact details:', error);
      throw error;
    }
  }
}

// Create singleton instance
const contactDetailsService = new ContactDetailsService();

export default contactDetailsService;

